"use client";

import { ExternalLink, Star } from "lucide-react";
import Image from "next/image";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import type { Tool } from "@/lib/tools";
import { useState, useEffect } from "react";
import { slugify } from "@/lib/utils";

// Helper function for initial truncation (works on server and client)
const truncateText = (text: string, maxLength: number = 150): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export function ToolCard({ tool }: { tool: Tool }) {
  // Do basic truncation for server rendering to avoid hydration mismatch
  const initialDescription = truncateText(tool.description);
  const [truncatedDescription, setTruncatedDescription] = useState(initialDescription);

  useEffect(() => {
    // Ensure consistent truncation on client side
    const truncated = truncateText(tool.description);
    setTruncatedDescription(truncated);
  }, [tool.description]);

  return (
    <Card className="h-full flex flex-col border-[#A4AFB7] hover:border-zinc-900 transition-colors rounded-[15px]">
      <CardHeader className="relative space-y-4">
        <div className="flex justify-between items-start">
          <Link
            href={`/ai-tools/category/${slugify(tool.category)}`}
          >
            <Badge 
              variant="outline" 
              className="w-fit text-[14px] text-[#536066] px-[15px] py-[5px] border border-[#A4AFB7] rounded-[15px] bg-white hover:bg-[#B8EF52]/10 hover:border-[#B8EF52] transition-colors"
            >
              {tool.category}
            </Badge>
          </Link>
          
          {tool.is_featured && (
            <Badge
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-[12px] py-[4px] rounded-[15px] pointer-events-none flex items-center gap-1"
            >
              <Star className="h-3.5 w-3.5 fill-white" />
              Featured
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Link 
            href={`/ai-tools/${slugify(tool.name)}`}
          >
            <Image
              src={tool.favicon_url || "/images/bolt-new-favicon.svg"}
              alt={`${tool.name} icon`}
              width={50}
              height={50}
              style={{ height: 'auto' }}
              className="shrink-0"
            />
          </Link>
          <div className="flex items-center gap-2">
            <Link 
              href={`/ai-tools/${slugify(tool.name)}`}
            >
              <h3 className="text-lg font-medium">{tool.name}</h3>
            </Link>
            {tool.is_verified && (
              <Image
                src="/images/verified.png"
                alt="Verified"
                width={20}
                height={20}
                style={{ height: 'auto' }}
                className="shrink-0 -ml-1"
              />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-[15px] text-muted-foreground">{truncatedDescription}</p>
      </CardContent>
      <CardFooter className="flex justify-between items-center gap-2">
        <Badge 
          variant="outline"
          className="rounded-[50px] text-[13px] px-[15px] py-[5px] border-none bg-[#ECF0F1] text-[#536066]"
        >
          {tool.pricing}
        </Badge>
        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="flex-1 text-right">
          <Button 
            className="rounded-[50px] bg-[#B8EF52] text-zinc-900 hover:bg-[#a5d649] px-[30px] py-[20px] flex items-center gap-2"
            size="sm"
          >
            Visit {tool.name}
            <ExternalLink className="h-4 w-4" />
          </Button>
        </a>
      </CardFooter>
    </Card>
  );
}